import React, { useEffect, useRef, useState } from "react";
import { Zap, Target, Users, Award } from "lucide-react";
import "./WhyChooseUs.css";

const timelineData = [
    {
        id: 0,
        Icon: Zap,
        title: "Speed",
        description:
            "Reduce delivery timelines from years to months to weeks with accelerated methodologies and proven frameworks.",
        enhancement: "Fast Track to Results",
    },
    {
        id: 1,
        Icon: Target,
        title: "Innovation",
        description:
            "Stay ahead of technology trends with cutting-edge solutions and strategic R&D. We deliver business outcomes through deep research and creativity.",
        enhancement: "Future-Ready Solutions",
    },
    {
        id: 2,
        Icon: Users, // lucide-react 'Users' used for partnership
        title: "Partnership",
        description:
            "We don't just advise — we co-build with you. Hands-on collaboration ensures successful execution and knowledge transfer.",
        enhancement: "Hands-On Collaboration",
    },
    {
        id: 3,
        Icon: Award,
        title: "Expertise",
        description:
            "Deep domain knowledge in SaaS, FinTech, HealthTech, and MarTech with a proven track record of successful transformations.",
        enhancement: "Proven Track Record",
    },
];

export default function WhyChooseUs2() {
    const total = timelineData.length;

    const [pinned, setPinned] = useState(null);        // pinned (clicked) point id or null
    const [hoverIndex, setHoverIndex] = useState(null);// temporary hover index
    const containerRef = useRef(null);

    // IntersectionObserver for animate-in elements inside component
    useEffect(() => {
        const root = containerRef.current || document;
        const nodes = root.querySelectorAll(".animate-in");

        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) entry.target.classList.add("visible");
                });
            },
            { threshold: 0.12, rootMargin: "0px 0px -50px 0px" }
        );

        nodes.forEach((n) => obs.observe(n));
        return () => obs.disconnect();
    }, []);

    // Helpers to compute which index is currently visible (pinned > hover)
    const visibleIndex = pinned ?? hoverIndex;

    const handleMouseEnter = (idx) => {
        setHoverIndex(idx);
    };

    const handleMouseLeave = () => {
        setHoverIndex(null);
    };

    const handleClick = (idx) => {
        setPinned((prev) => (prev === idx ? null : idx));
    };

    const handleKeyDown = (e, idx) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick(idx);
        }
    };

    return (
        <section className="section value-prop-section" ref={containerRef}>
            <div className="container">
                <div className="section-header animate-in">
                    <h2 className="section-title">Why Choose Cospin?</h2>
                    <p className="section-subtitle">
                        Our proven consulting approach delivers measurable outcomes through four pillars of excellence
                    </p>
                </div>

                <div className="timeline-container animate-in">
                    {/* timeline base without progress animation */}
                    <div className="timeline-line" aria-hidden>
                    </div>

                    <div className="timeline-points">
                        {timelineData.map((item, idx) => {
                            const active = visibleIndex === idx;
                            const Icon = item.Icon;
                            return (
                                <div
                                    key={item.id}
                                    className={`timeline-point ${active ? "active" : ""}`}
                                    data-step={idx + 1}
                                    tabIndex={0}
                                    role="button"
                                    aria-label={`Step ${idx + 1}: ${item.title}`}
                                    onMouseEnter={() => handleMouseEnter(idx)}
                                    onMouseLeave={handleMouseLeave}
                                    onClick={() => handleClick(idx)}
                                    onKeyDown={(e) => handleKeyDown(e, idx)}
                                    style={{ animationDelay: `${0.2 + idx * 0.08}s` }}
                                >
                                    <div className="timeline-dot" />
                                    <div className="timeline-card">
                                        <div className="timeline-icon">
                                            <Icon size={28} />
                                        </div>
                                        <h3 className="timeline-title">{item.title}</h3>
                                        <p className="timeline-description">{item.description}</p>
                                        <div className="timeline-enhancement">{item.enhancement}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
